import { defineStore } from 'pinia'

const RECOMMENDATIONS_CACHE = 'recommendations'

export const useRecommendationStore = defineStore('recommendations', {
  state() {
    return {
      ready: false,
      inProgress: false,
      data: {
        successor: [],
        bundle: [],
        simple: [],
        follow_up: []
      }
    }
  },
  getters: {
    getByType:
      ({ data }) =>
      (type) =>
        _getByType(type, data),
    hasPersonalizeRecommendations: ({
      data: { successor, bundle, simple, follow_up: fu }
    }) => successor.length + bundle.length + simple.length + fu.length > 0,
    hasMinimum:
      ({ data }) =>
      (type, min) =>
        _getByType(type, data).length >= min,
    updating: ({ inProgress }) => inProgress,
    fetched: ({ inProgress }) => inProgress
  },
  actions: {
    async fetch() {
      // Synchronous guard before any await — every viewed tile calls this,
      // so concurrent callers must bail here (and await waitUntilReady())
      // instead of stampeding the endpoint.
      if (this.ready || this.inProgress) return
      this.inProgress = true

      const { ApiController, CacheHelper, customerStore } = await _getModules()
      await customerStore.waitUntilPending()

      if (customerStore.customerSoftLoggedIn) {
        try {
          // Cache the auth-bound response per customer so collection-page
          // navigations / full reloads don't refetch within the TTL.
          const cacheId = _cacheId(customerStore)
          const cached = cacheId
            ? await CacheHelper.retrieve(RECOMMENDATIONS_CACHE, cacheId)
            : undefined

          if (cached) {
            this.data = _prepare(cached)
          } else {
            const response = await ApiController.fetchRecommendations()
            this.data = _prepare(response)
            if (cacheId) {
              await CacheHelper.write(RECOMMENDATIONS_CACHE, cacheId, response)
            }
          }
        } catch (e) {
          console.error(e)
        }
      }

      this.ready = true
      this.inProgress = false
    },
    async waitUntilReady() {
      while (!this.ready) await _wait()
      return Promise.resolve()
    }
  }
})

const _wait = async () =>
  new Promise((resolve) => {
    setTimeout(resolve, 100)
  })

const _getByType = (type, data) => {
  if (!type)
    return [
      ...data.successor,
      ...data.bundle,
      ...data.simple,
      ...data.follow_up
    ]

  return data[type] ?? []
}

const _prepare = ({
  successor = [],
  bundle = [],
  simple = [],
  follow_up: fu = [],
  single_campaign_id: sCId,
  bundle_campaign_id: bCId
}) => ({
  successor: _mapper(successor, sCId),
  bundle: _mapper(bundle, bCId),
  simple: _mapper(simple, sCId),
  follow_up: _mapper(fu, sCId)
})

const _mapper = (ref, cId) =>
  ref?.map(({ product_id: pId }) => ({ product_id: pId, campaign_id: cId }))

// Customer-scoped cache id so users on a shared browser never see each
// other's recommendations. No id (e.g. session edge cases) → no caching.
const _cacheId = ({ customerId }) =>
  customerId ? `customer-${customerId}` : null

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [
      { default: ApiController },
      { default: CacheHelper },
      { useCustomerStore }
    ] = await Promise.all([
      import('@/services/ApiController'),
      import('@/services/helpers/CacheHelper'),
      import('@/stores/customer')
    ])
    cache = {
      ApiController,
      CacheHelper,
      customerStore: useCustomerStore()
    }
    return cache
  }
})()
